import { z } from "zod/v3";
import { isRichTextContentEmpty } from "@/lib/htmlHelpers";
import { validateUrl } from "./utils";

const DateSchema = z
  .object({
    startDate: z.string().nullable().optional(),
    endDate: z.string().nullable().optional()
  })
  .optional();

// Helper for fields that can be either string or null
const nullableString = z.union([z.string(), z.null()]).transform((val) => (val === null ? "" : val));

const ApplicationQuestionSchema = z.object({
  id: z.string().optional(),
  questionType: z.string(),
  content: z.string(),
  options: z.array(z.object({ content: z.string() })).optional()
});

// Shared base shape for both schemas
const baseShape = {
  name: z.string().min(1, "Job title is required"), // required for both
  facilityId: z.string().min(1, "Facility is required"), // required for both
  stage: z.string(),
  serviceLevel: z.enum(["Advanced", "Executive"]), // required for both
  benefits: nullableString.optional(),
  additionalIncome: nullableString.optional(),
  overview: z.string().refine((val) => !isRichTextContentEmpty(val), {
    message: "Overview is required"
  }), // required for both
  pointOfContact: z.object({
    name: z.string().min(1, "Contact name is required"), // required for both
    email: z.string().email("Invalid email address"), // required for both
    title: z.string().optional(),
    phone: z.string().optional()
  }),
  employerId: z.string().optional(),
  employerData: z.any().nullable(),
  category: z.string().min(1, "Job category is required"), // required for both
  hideCompensationFromJobBoard: z.boolean(), // required for both
  applyVia: z.string().refine((val) => val === "Facility Website" || val === "PGA.org / Email Notification", {
    message: "Apply via is required"
  }), // required for both
  externalJobUrl: nullableString.optional().refine((val) => (val ? validateUrl(val) === true : true), {
    message: "Application URL is not valid"
  }),
  paySchedule: z.string(),
  positionType: z.string().min(1, "Position type is required"), // required for both
  otherSections: z.array(z.string()),
  otherStates: z.array(z.string()),
  additionalPgaEducation: nullableString.optional(),
  sendForReview: z.boolean(),
  sendForReviewDescription: z.string(),
  sectionId: z.string().min(1, "Section is required"), // required for both
  application: z
    .object({
      id: z.string().optional(),
      questions: z.array(ApplicationQuestionSchema).optional()
    })
    .optional()
};

// Draft schema: only fields required for Save (draft) are required
export const AdvancedJobPostDraftSchema = z.object({
  ...baseShape,
  compensation: z.object({
    baseMaximum: z.number().optional(),
    baseMinimum: z.number().optional(),
    totalAnticipated: z.number().optional(),
    currencyUnit: z.string()
  }),
  overview: z.string().optional(),
  requiredEducation: z.string().optional(),
  requiredExperienceInYears: z.string().optional(),
  requiredExperienceDescription: nullableString.optional(),
  essentialResponsibilities: nullableString.optional(),
  applicantGroup: nullableString.optional(),
  submissionDeadline: DateSchema,
  externalJobUrl: nullableString.optional()
});

// Publish schema: fields required for Save & Publish are required
export const AdvancedJobPostPublishSchema = z
  .object({
    ...baseShape,
    overview: z.string().refine((val) => !isRichTextContentEmpty(val), {
      message: "Overview is required"
    }),
    requiredEducation: z.string().optional(),
    requiredExperienceInYears: z.string().min(1, "Years of experience is required"),
    requiredExperienceDescription: nullableString.optional(),
    essentialResponsibilities: nullableString.optional(),
    applicantGroup: z.string().min(1, "Applicant group is required"),
    submissionDeadline: DateSchema.refine((val) => val && (val.startDate || val.endDate), {
      message: "Submission deadline is required"
    }),
    externalJobUrl: nullableString.optional().refine((val) => (val ? validateUrl(val) === true : true), {
      message: "Application URL is not valid"
    }),
    compensation: z
      .object({
        baseMaximum: z.number().min(0.01, "Base compensation maximum is required"),
        baseMinimum: z.number().min(0.01, "Base compensation minimum is required"),
        totalAnticipated: z.number().min(0.01, "Total anticipated compensation is required")
      })
      .superRefine((data, ctx) => {
        if (data.baseMaximum < data.baseMinimum) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Base Maximum must be greater than Base Minimum",
            path: ["baseMaximum"]
          });
        }
      })
  })
  .refine(
    (data) =>
      data.applyVia === "Facility Website" ? data.externalJobUrl !== null && data.externalJobUrl !== "" : true,
    {
      message: "Must include URL when Facility Website is chosen.",
      path: ["externalJobUrl"]
    }
  );
